import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { deferredDeepLinkService } from './deferredDeepLink';

export interface NotificationNavigationData {
  taskId?: string;
  [key: string]: any;
}

class NavigationService {
  private static instance: NavigationService;
  private navigationCallback?: (path: string) => void;
  private pendingNavigation?: string;
  private isInitialized = false;
  private appStateListenerHandle: any = null;
  private appUrlOpenListenerHandle: any = null;

  private constructor() {}

  static getInstance(): NavigationService {
    if (!NavigationService.instance) {
      NavigationService.instance = new NavigationService();
    }
    return NavigationService.instance;
  }

  // Initialize the navigation service
  async initialize() {
    if (this.isInitialized) return;
    
    console.log('Initializing navigation service...');
    
    // Initialize deferred deep link service first
    await deferredDeepLinkService.initialize();
    
    if (Capacitor.isNativePlatform()) {
      // Remove existing listeners if any
      if (this.appStateListenerHandle) {
        this.appStateListenerHandle.remove();
        this.appStateListenerHandle = null;
      }
      if (this.appUrlOpenListenerHandle) {
        this.appUrlOpenListenerHandle.remove();
        this.appUrlOpenListenerHandle = null;
      }
      
      // Listen for app state changes
      this.appStateListenerHandle = App.addListener('appStateChange', ({ isActive }) => {
        console.log('App state changed, isActive:', isActive, 'pendingNavigation:', this.pendingNavigation);
        if (isActive && this.pendingNavigation) {
          console.log('App became active, handling pending navigation:', this.pendingNavigation);
          this.handlePendingNavigation();
        }
      });

      // Listen for deep links
      this.appUrlOpenListenerHandle = App.addListener('appUrlOpen', ({ url }) => {
        console.log('Deep link received:', url);
        this.handleDeepLink(url);
      });

      // Handle cold start launch URL (moved from App.tsx)
      this.handleLaunchUrl();
    }
    
    this.isInitialized = true;
    console.log('Navigation service initialized');
  }

  // Handle launch URL for cold start (moved from App.tsx)
  private async handleLaunchUrl() {
    try {
      const launch = await App.getLaunchUrl();
      if (launch?.url) {
        console.log('Launch URL found:', launch.url);
        this.handleDeepLink(launch.url);
      }
    } catch (error) {
      console.warn("Failed to get launch URL:", error);
    }
  }

  // Clean up listeners
  cleanup() {
    if (this.appStateListenerHandle) {
      this.appStateListenerHandle.remove();
      this.appStateListenerHandle = null;
    }
    if (this.appUrlOpenListenerHandle) {
      this.appUrlOpenListenerHandle.remove();
      this.appUrlOpenListenerHandle = null;
    }
    this.isInitialized = false;
    console.log('Navigation service cleaned up');
  }

  // Set the navigation callback from React Router
  setNavigationCallback(callback: (path: string) => void) {
    this.navigationCallback = callback;
    
    // If there's a pending navigation, handle it now
    if (this.pendingNavigation) {
      this.handlePendingNavigation();
    }
  }

  // Handle navigation from notifications (shift / patrol payloads only for now)
  handleNotificationNavigation(data?: NotificationNavigationData) {
    console.log('Handling notification navigation with data:', data);

    let targetPath: string;

    const category = data?.notification_category as string | undefined;
    const kind = (data?.notification_kind ?? data?.type) as string | undefined;
    const isPatrolCategory = category === 'patrol' || kind?.startsWith('patrol_');
    const isShiftCategory = category === 'shift' || kind?.startsWith('shift_');
    const isLeaveCategory = category === 'leave' || kind?.startsWith('leave_');

    if (isPatrolCategory) {
      const openScanner =
        kind === 'patrol_overdue' || data?.type === 'patrol_overdue';
      targetPath = openScanner ? '/shifts?openPatrolScanner=1' : '/shifts';
    } else if (isLeaveCategory) {
      const leaveRequestId =
        data?.leaveRequestId ??
        data?.leave_request_id ??
        data?.entity_id;
      if (leaveRequestId != null && Number.isFinite(Number(leaveRequestId))) {
        targetPath = `/shifts?leaveRequestId=${Number(leaveRequestId)}`;
      } else {
        targetPath = '/shifts';
      }
    } else if (isShiftCategory) {
      targetPath = '/shifts';
    } else {
      targetPath = '/shifts';
    }

    console.log('Navigating to:', targetPath);

    try {
      if (this.navigationCallback) {
        // App is open, use React Router navigation
        console.log('Using React Router navigation callback');
        this.navigationCallback(targetPath);
      } else {
        // App is closed/background, store the navigation intent
        console.log('No navigation callback available, storing pending navigation');
        this.pendingNavigation = targetPath;
        this.navigateToPath(targetPath);
      }
    } catch (error) {
      console.error('Error during navigation:', error);
      if (this.navigationCallback) {
        this.navigationCallback('/shifts');
      }
    }
  }

  // Emergency fallback navigation - ensures app always opens
  private emergencyNavigation() {
    console.log('Emergency navigation triggered - ensuring app opens');
    
    // Try multiple fallback paths in order of preference
    const fallbackPaths = ['/shifts', '/messages', '/'];
    
    for (const path of fallbackPaths) {
      try {
        if (this.navigationCallback) {
          console.log(`Attempting emergency navigation to: ${path}`);
          this.navigationCallback(path);
          return;
        }
      } catch (error) {
        console.error(`Emergency navigation to ${path} failed:`, error);
      }
    }
    
    console.error('All emergency navigation attempts failed');
  }

  // Handle pending navigation when app becomes active
  private handlePendingNavigation() {
    if (this.pendingNavigation && this.navigationCallback) {
      console.log('Executing pending navigation:', this.pendingNavigation);
      
      // Add a small delay to ensure the app is fully loaded
      setTimeout(() => {
        this.navigationCallback!(this.pendingNavigation!);
        this.pendingNavigation = undefined;
      }, 1000);
    } else if (this.pendingNavigation && !this.navigationCallback) {
      console.log('Pending navigation exists but no callback yet, waiting...');
      
      // Wait for navigation callback to be set
      const checkCallback = () => {
        if (this.navigationCallback) {
          console.log('Navigation callback now available, executing pending navigation');
          setTimeout(() => {
            this.navigationCallback!(this.pendingNavigation!);
            this.pendingNavigation = undefined;
          }, 1000);
        } else {
          // Check again in 500ms
          setTimeout(checkCallback, 500);
        }
      };
      
      checkCallback();
    }
  }

  // Handle deep link URLs
  private handleDeepLink(url: string) {
    try {
      // Parse the deep link URL to extract the path
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      
      console.log('Handling deep link path:', path);
      
      // Handle invite URLs (moved from App.tsx to avoid duplicate listeners)
      const HOST = "sawadone-4d94d35e4cba.herokuapp.com";
      if (urlObj.hostname === HOST && path.startsWith("/e/")) {
        const uid = path.split("/")[2] || "";
        if (uid) {
          this.handleInviteUrl(uid);
          return;
        }
      }
      
      if (this.navigationCallback) {
        this.navigationCallback(path);
      } else {
        this.pendingNavigation = path;
      }
    } catch (error) {
      console.error('Error handling deep link:', error);
    }
  }

  // Handle invite URL logic (moved from App.tsx)
  private handleInviteUrl(uid: string) {
    console.log('Handling invite URL with UID:', uid);
    
    // Store the UID in multiple places for persistence
    localStorage.setItem("pendingInviteUid", uid);
    localStorage.setItem("deferredInviteUid", uid);
    
    // Also store with timestamp for debugging
    localStorage.setItem("inviteTimestamp", Date.now().toString());
    
    // Store in deferred deep link service for better persistence
    deferredDeepLinkService.setDeferredData({
      uid,
      source: 'intent',
      timestamp: Date.now()
    });
    
    // SPA navigation – no reload
    const url = `/initialize?uid=${encodeURIComponent(uid)}`;
    
    // Check if we're in a browser environment and location is available
    if (typeof window !== 'undefined' && window.location) {
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      const expectedSearch = `?uid=${encodeURIComponent(uid)}`;
      
      if (currentPath !== "/initialize" || currentSearch !== expectedSearch) {
        window.history.replaceState({}, "", url);
      }
    }
    
    // Force navigation to initialize page if we have a navigation callback
    if (this.navigationCallback) {
      console.log('Forcing navigation to initialize page with UID');
      this.navigationCallback(`/initialize?uid=${encodeURIComponent(uid)}`);
    } else {
      console.log('No navigation callback available, storing pending navigation');
      this.pendingNavigation = `/initialize?uid=${encodeURIComponent(uid)}`;
    }
  }

  private navigateToPath(path: string) {
    if (Capacitor.isNativePlatform()) {
      // For native platforms, we need to trigger the app to open with the deep link
      // The actual navigation will be handled by the deep link listener
      console.log('Native platform navigation to:', path);
      
      // Store the navigation intent - it will be handled when the app becomes active
      this.pendingNavigation = path;
      
      // For now, we can't programmatically open the app from a notification
      // The app should already be opening due to the notification click
      // The deep link handling will take care of the navigation
    } else {
      // For web, open the app with the specific path
      if (typeof window !== 'undefined' && window.location) {
        const baseUrl = window.location.origin;
        const fullUrl = `${baseUrl}${path}`;
        console.log('Web platform navigation to:', fullUrl);
        
        // Open the app window with the specific path
        if ('clients' in self) {
          // This is in a service worker context
          (self as any).clients.openWindow(fullUrl);
        } else {
          // This is in the main app context
          window.location.href = fullUrl;
        }
      } else {
        console.warn('Cannot navigate: window.location not available');
      }
    }
  }
}

export const navigationService = NavigationService.getInstance();
